import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { Modal } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';
import { AgentContent } from './AgentContent';
import { MessageInput } from './input/MessageInput';
import AgentWidgetList from './AgentWidgetList';

type ViewDirection = 'forward' | 'back';

export interface AgentWidgetModalProps {
  open: boolean;
  onClose: () => void;
  modalDraggable?: boolean;
  modalPinnable?: boolean;
  modalDefaultPinned?: boolean;
  modalInitialOffsetX?: number;
  modalInitialOffsetY?: number;
  modalOpacityAdjustable?: boolean;
  modalDefaultOpacity?: number;
  modalDefaultWidth?: number;
  modalDefaultHeight?: number;
  modalMinWidth?: number;
  modalMinHeight?: number;
  modalCloseOnBackdrop?: boolean;
  modalDimBackdrop?: boolean;
  modalPassthroughBackdrop?: boolean;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
  mountMode?: 'viewport' | 'parent';
  hostBounds?: { width: number; height: number };
  modalOnSizeChange?: (size: { w: number; h: number }) => void;
  modalOnOffsetChange?: (offset: { x: number; y: number }) => void;
  modalOnPinnedChange?: (pinned: boolean) => void;
  modalOnOpacityChange?: (opacity: number) => void;
  modalLayerZIndex?: number;
  modalOnActivate?: () => void;
}

export const AgentWidgetModal: React.FC<AgentWidgetModalProps> = ({
  open,
  onClose,
  modalDraggable = false,
  modalPinnable = false,
  modalDefaultPinned = false,
  modalInitialOffsetX = 0,
  modalInitialOffsetY = 0,
  modalOpacityAdjustable = false,
  modalDefaultOpacity = 1,
  modalDefaultWidth = 780,
  modalDefaultHeight = 560,
  modalMinWidth = 560,
  modalMinHeight = 420,
  modalCloseOnBackdrop = true,
  modalDimBackdrop = true,
  modalPassthroughBackdrop = false,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
  mountMode = 'viewport',
  hostBounds,
  modalOnSizeChange,
  modalOnOffsetChange,
  modalOnPinnedChange,
  modalOnOpacityChange,
  modalLayerZIndex,
  modalOnActivate,
}) => {
  const { t } = useTranslation(['common', 'widget']);
  const activeConversation = useChatStore((s) => (
    s.activeId ? s.conversations[s.activeId] ?? null : null
  ));
  const agentConversations = useChatStore((s) =>
    s.conversationOrder
      .map((id) => s.conversations[id])
      .filter((conversation) =>
        !!conversation
        && conversation.mode === 'agent'
        && conversation.workspaceKind === 'page')
  );
  const loadFromBackendApi = useChatStore((s) => s.loadFromBackendApi);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);

  const [activeView, setActiveView] = useState<'list' | 'detail'>('list');
  const [viewDirection, setViewDirection] = useState<ViewDirection>('forward');

  useEffect(() => {
    if (!open) return;
    if (agentConversations.length === 0) {
      setActiveConversation(null, { syncMode: false });
      setActiveView('detail');
    } else {
      setActiveView('list');
    }
    setViewDirection('forward');
    void loadFromBackendApi();
  }, [agentConversations.length, loadFromBackendApi, open, setActiveConversation]);

  const detailTitle = useMemo(() => {
    if (!activeConversation) {
      return t('common:chat.newConversation');
    }
    return activeConversation.title || activeConversation.sharedGroup?.title || t('common:chat.newConversation');
  }, [activeConversation]);

  const enterDetail = useCallback(() => {
    setViewDirection('forward');
    setActiveView('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewDirection('back');
    setActiveView('list');
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('widget:agentWidget.title')}
      width={`${modalDefaultWidth}px`}
      resizable
      defaultHeight={modalDefaultHeight}
      minWidth={modalMinWidth}
      minHeight={modalMinHeight}
      draggable={modalDraggable}
      pinnable={modalPinnable}
      defaultPinned={modalDefaultPinned}
      initialOffsetX={modalInitialOffsetX}
      initialOffsetY={modalInitialOffsetY}
      opacityAdjustable={modalOpacityAdjustable}
      defaultOpacity={modalDefaultOpacity}
      closeOnBackdrop={modalCloseOnBackdrop}
      dimBackdrop={modalDimBackdrop}
      passthroughBackdrop={modalPassthroughBackdrop}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
      mountMode={mountMode}
      hostBounds={hostBounds}
      bodyPadding="0 12px 12px"
      onSizeChange={modalOnSizeChange}
      onOffsetChange={modalOnOffsetChange}
      onPinnedChange={modalOnPinnedChange}
      onOpacityChange={modalOnOpacityChange}
      layerZIndex={modalLayerZIndex}
      onActivate={modalOnActivate}
    >
      <ModalBody>
        <Viewport>
          <AnimatedView
            key={`${activeView}-${viewDirection}`}
            $direction={viewDirection}
          >
            {activeView === 'list' ? (
              <ListView>
                <AgentWidgetList
                  onSelectConversation={enterDetail}
                  onCreateConversation={enterDetail}
                />
              </ListView>
            ) : (
              <DetailView>
                <DetailTopBar>
                  <BackBtn
                    type="button"
                    onClick={handleBackToList}
                    title={t('widget:dm.backToList')}
                    aria-label={t('widget:dm.backToList')}
                  >
                    <ArrowLeft size={16} />
                  </BackBtn>
                  <DetailTopTitle>{detailTitle}</DetailTopTitle>
                </DetailTopBar>
                <DetailContentArea>
                  <AgentContent />
                </DetailContentArea>
                <InputArea>
                  <MessageInput modeOverride="agent" surfaceOpacity={modalDefaultOpacity} />
                  <InputDisclaimer>{t('common:chat.aiDisclaimer')}</InputDisclaimer>
                </InputArea>
              </DetailView>
            )}
          </AnimatedView>
        </Viewport>
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Viewport = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const AnimatedView = styled.div<{ $direction: ViewDirection }>`
  height: 100%;
  min-height: 0;
  animation: ${({ $direction }) => ($direction === 'forward' ? slideInFromRight : slideInFromLeft)} 230ms
    cubic-bezier(0.22, 1, 0.36, 1);
`;

const ListView = styled.div`
  height: 100%;
  min-height: 0;
`;

const DetailView = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const DetailTopBar = styled.div`
  height: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 8px;
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  background: transparent;
`;

const BackBtn = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
  }
`;

const DetailTopTitle = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const InputArea = styled.div`
  flex-shrink: 0;
`;

const InputDisclaimer = styled.div`
  font-size: 0.6875rem;
  text-align: center;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.94)' : 'rgba(0,0,0,0.88)'};
  padding-bottom: 6px;
`;

export default AgentWidgetModal;
